import React, { useContext, useMemo } from 'react'
import { Formik, Form, Field } from 'formik'
import { StaticTextareaField, FieldWrapper, ErrorHelp } from './Form'
import { CallSubjectManagerContext } from '~/service/CallSubjectManager'
import ButtonSelect from './Form/ButtonSelect'
import trans from '~/translations/trans'
import Rating, { StaticRatingField } from './Form/Rating'
import { TreeSelect } from 'antd'
import { createDataTree } from '~/utils/array'
import { commonInitial } from './common'
import { DescribeSimpleValues, FormProps, StaticProps } from './types'
import FieldNote from './Form/FieldNote'
import Submit from './Submit'

export const DescribeSimpleStatic: React.FC<StaticProps> = ({ call }) => {
  const { subjects } = useContext(CallSubjectManagerContext)

  return (
    <>
      {subjects.length > 0 && (
        <StaticTextareaField
          label={intl.formatMessage({ id: 'call / describe / subject' })}
          value={
            call.subjects.length
              ? call.subjects
                  .map(id => subjects.find(subject => subject.id == id)?.name)
                  .join(', ')
              : '-'
          }
          horizontal={false}
        />
      )}

      <StaticTextareaField
        label={intl.formatMessage({ id: 'call / describe / note' })}
        value={call.note ? call.note : '-'}
        horizontal={false}
      />

      <StaticRatingField value={call.rating} />
    </>
  )
}

const simpleInitial = {
  ...commonInitial,
  subjects: [],
}

const DescribeSimple: React.FC<FormProps> = ({
  call,
  formikConfig,
  showSubmit,
  submitRef,
}) => {
  const { subjects } = useContext(CallSubjectManagerContext)

  const tree = useMemo(
    () =>
      createDataTree(
        subjects
          .filter(
            subject =>
              subject.createdAt.getTime() < call.createdAt.getTime() &&
              (!subject.removedAt ||
                subject.removedAt.getTime() > call.createdAt.getTime())
          )
          .map(subject => ({
            id: subject.id,
            title: subject.name,
            value: subject.id,
            parentId: subject.parentId,
          }))
      ),
    []
  )

  const initialValues = {
    ...simpleInitial,
    ...formikConfig.initialValues,
    type: 'simple',
  }

  return (
    <Formik<DescribeSimpleValues>
      {...formikConfig}
      validate={values => {
        formikConfig.validate && formikConfig.validate(values)

        const errors: any = {}

        if (values.success && subjects.length) {
          if (!values.subjects.length) {
            errors.subjects = intl.formatMessage({
              id: 'call / describe / inbound / subject / validation / min one',
            })
          }
        }

        return errors
      }}
      initialValues={initialValues}
    >
      {({
        isSubmitting,
        values,
        setFieldValue,
        errors,
        touched,
        submitForm,
      }) => (
        <Form>
          <>
            {(() => {
              if (submitRef) {
                submitRef.current = submitForm
              }
            })()}

            {subjects.length > 0 && (
              <>
                <Field
                  label={intl.formatMessage({
                    id: 'call / describe / is success',
                  })}
                  name="success"
                  horizontal={false}
                  component={ButtonSelect}
                  options={[
                    [true, trans.form.yes()],
                    [false, trans.form.no()],
                  ]}
                  afterChange={(val: boolean) => {
                    if (!val) {
                      setFieldValue('subjects', [])
                    }
                  }}
                />

                {values.success && (
                  <FieldWrapper
                    label={intl.formatMessage({
                      id: 'call / describe / subject',
                    })}
                    horizontal={false}
                  >
                    <div className="field">
                      <div className="control">
                        <TreeSelect
                          treeData={tree}
                          style={{ width: '100%' }}
                          allowClear
                          treeDefaultExpandAll
                          showSearch
                          multiple
                          value={values.subjects}
                          onChange={val => setFieldValue('subjects', val)}
                          filterTreeNode={(search, item) =>
                            !!(item['title'] as string)
                              .removeAccents()
                              .match(
                                new RegExp(
                                  search.removeAccents().escapeRegExp(),
                                  'i'
                                )
                              )
                          }
                        />
                      </div>

                      <ErrorHelp
                        errors={errors}
                        touched={touched}
                        name="subjects"
                      />
                    </div>
                  </FieldWrapper>
                )}
              </>
            )}

            <FieldNote />

            <Field name="rating" component={Rating} />

            {showSubmit && <Submit isSubmitting={isSubmitting} />}
          </>
        </Form>
      )}
    </Formik>
  )
}

//

export default DescribeSimple
